import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import {
  ArrowLeft,
  ExternalLink,
  AtSign,
  Briefcase,
  Pencil,
  Trash2,
  Send,
  X,
  ChevronLeft,
  ChevronRight,
  MessageSquare,
} from "lucide-react";
import {
  STANDUP_SECTION_LABELS,
  type Standup,
  type StandupItem,
  type StandupItemTicket,
  type StandupItemMention,
  type StandupAttachment,
  type SessionUser,
} from "@axorial/shared";
import { PageHeader } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { formatDate } from "@/lib/format";

export const Route = createFileRoute("/standups/$standupId")({
  component: StandupDetailPage,
});

interface StandupDetail {
  standup: Standup;
  authorName: string;
  authorEmail: string;
  items: StandupItem[];
  tickets: StandupItemTicket[];
  mentions: StandupItemMention[];
  attachments: StandupAttachment[];
  mentionUsers: { id: string; name: string; email: string }[];
  projects: { id: string; name: string; slug: string }[];
  replies: Array<{
    id: string;
    standupId: string;
    mattermostPostId: string;
    mattermostUserId: string;
    authorUsername: string;
    authorDisplayName: string | null;
    userId: string | null;
    message: string;
    createdAtMm: number;
    updatedAtMm: number | null;
    ingestedAt: number;
    permalink: string | null;
  }>;
  standupPermalink: string | null;
}

// Mismo orden que el formulario. `kind` controla el tipo de render.
const SECTIONS: Array<
  | { kind: "items"; key: "yesterday" | "today"; label: string }
  | {
      kind: "text";
      key:
        | "yesterdayBlockers"
        | "generalDifficulties"
        | "todayBlockers"
        | "toolingFriction"
        | "comments";
      label: string;
    }
> = [
  { kind: "items", key: "yesterday", label: STANDUP_SECTION_LABELS.yesterday },
  { kind: "text", key: "yesterdayBlockers", label: STANDUP_SECTION_LABELS.yesterday_blockers },
  { kind: "text", key: "generalDifficulties", label: STANDUP_SECTION_LABELS.general_difficulties },
  { kind: "items", key: "today", label: STANDUP_SECTION_LABELS.today },
  { kind: "text", key: "todayBlockers", label: STANDUP_SECTION_LABELS.today_blockers },
  { kind: "text", key: "toolingFriction", label: STANDUP_SECTION_LABELS.tooling_friction },
  { kind: "text", key: "comments", label: STANDUP_SECTION_LABELS.comments },
];

/** Grid de previews de attachments. Para imágenes, click abre el viewer
 * modal en lugar de una pestaña nueva (más rápido + soporta navegación
 * prev/next con teclado). Archivos no-imagen siguen abriéndose externos
 * porque no podemos previewearlos inline.
 *
 * El viewer vive dentro del grid — cada grid (sección o item) tiene su
 * propia ventana de navegación. Cruzar attachments entre secciones
 * desorientaría al usuario que vino de un click puntual. */
function AttachmentsGrid({ attachments }: { attachments: StandupAttachment[] }) {
  const images = attachments.filter((a) => a.mimeType.startsWith("image/"));
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  if (attachments.length === 0) return null;
  return (
    <>
      <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
        {attachments.map((a) => {
          const isImage = a.mimeType.startsWith("image/");
          const imageIndex = isImage ? images.findIndex((i) => i.id === a.id) : -1;
          return (
            <li
              key={a.id}
              className="rounded border border-base-300/60 overflow-hidden bg-base-100"
            >
              {isImage ? (
                <button
                  type="button"
                  onClick={() => setViewerIndex(imageIndex)}
                  className="block w-full text-left"
                >
                  <img
                    src={`/api/standups/attachments/${a.id}`}
                    alt={a.filename}
                    className="w-full aspect-video object-cover hover:opacity-90 transition-opacity"
                  />
                </button>
              ) : (
                <a
                  href={`/api/standups/attachments/${a.id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="block"
                >
                  <div className="aspect-video grid place-items-center text-xs text-base-content/55 p-2">
                    {a.filename}
                  </div>
                </a>
              )}
              <div
                className="px-1.5 py-0.5 text-[10px] truncate text-base-content/55"
                title={a.filename}
              >
                {a.filename}
              </div>
            </li>
          );
        })}
      </ul>
      {viewerIndex !== null && (
        <ImageViewerModal
          images={images}
          index={viewerIndex}
          onClose={() => setViewerIndex(null)}
          onIndexChange={setViewerIndex}
        />
      )}
    </>
  );
}

/** Modal de imagen full-screen con navegación prev/next (botones + teclado).
 * Click fuera o ESC cierra. */
function ImageViewerModal({
  images,
  index,
  onClose,
  onIndexChange,
}: {
  images: StandupAttachment[];
  index: number;
  onClose: () => void;
  onIndexChange: (i: number) => void;
}) {
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft" && index > 0) onIndexChange(index - 1);
      else if (e.key === "ArrowRight" && index < images.length - 1) onIndexChange(index + 1);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, images.length, onClose, onIndexChange]);

  const current = images[index];
  if (!current) return null;
  const hasPrev = index > 0;
  const hasNext = index < images.length - 1;
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 animate-in fade-in"
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 btn btn-circle btn-sm bg-base-100/20 hover:bg-base-100/40 border-0 text-white"
        aria-label="Cerrar"
      >
        <X className="size-5" />
      </button>
      {hasPrev && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onIndexChange(index - 1);
          }}
          className="absolute left-4 btn btn-circle btn-sm bg-base-100/20 hover:bg-base-100/40 border-0 text-white"
          aria-label="Anterior"
        >
          <ChevronLeft className="size-5" />
        </button>
      )}
      {hasNext && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onIndexChange(index + 1);
          }}
          className="absolute right-4 btn btn-circle btn-sm bg-base-100/20 hover:bg-base-100/40 border-0 text-white"
          aria-label="Siguiente"
        >
          <ChevronRight className="size-5" />
        </button>
      )}
      <div
        className="max-w-[95vw] max-h-[90vh] flex flex-col items-center gap-2"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={`/api/standups/attachments/${current.id}`}
          alt={current.filename}
          className="max-w-full max-h-[85vh] object-contain rounded shadow-2xl"
        />
        <div className="text-xs text-white/70 flex items-center gap-3">
          <span className="truncate max-w-[60vw]">{current.filename}</span>
          {images.length > 1 && (
            <span className="tabular-nums">
              {index + 1} / {images.length}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function StandupDetailPage() {
  const { standupId } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<StandupDetail>({
    queryKey: ["standup", standupId],
    queryFn: () => api.get(`/standups/${standupId}`),
  });
  const { data: me } = useQuery<SessionUser>({
    queryKey: ["me"],
    queryFn: () => api.get("/me"),
  });
  const del = useMutation({
    mutationFn: () => api.del(`/standups/${standupId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["standups"] });
      qc.invalidateQueries({ queryKey: ["standup", "me", "today"] });
      navigate({ to: "/standups" });
    },
  });
  const repost = useMutation({
    mutationFn: () => api.post(`/standups/${standupId}/repost`),
    onSuccess: () => {
      // El job es async (queue). Damos feedback de "encolado" — el post
      // aparece en Mattermost en segundos.
      window.alert("Re-postear encolado. El standup se publicará en Mattermost en breve.");
    },
  });

  if (isLoading || !data) {
    return (
      <div className="grid place-items-center py-20">
        <span className="loading loading-spinner loading-lg text-primary" />
      </div>
    );
  }
  const {
    standup,
    authorName,
    items,
    tickets,
    mentions,
    mentionUsers,
    projects,
    attachments,
    replies,
    standupPermalink,
  } = data;

  const userById = new Map(mentionUsers.map((u) => [u.id, u]));
  const projectById = new Map(projects.map((p) => [p.id, p]));
  const mentionsByItem = new Map<string, string[]>();
  for (const m of mentions) {
    const arr = mentionsByItem.get(m.itemId) ?? [];
    arr.push(m.userId);
    mentionsByItem.set(m.itemId, arr);
  }
  // Adjuntos por slot. Los de item tienen item_id; los de sección tienen
  // section + item_id NULL. Usamos `${kind}:${id}` como clave para evitar
  // ambigüedad entre IDs.
  const attsByItem = new Map<string, typeof attachments>();
  const attsBySection = new Map<string, typeof attachments>();
  for (const a of attachments) {
    if (a.itemId) {
      const arr = attsByItem.get(a.itemId) ?? [];
      arr.push(a);
      attsByItem.set(a.itemId, arr);
    } else if (a.section) {
      const arr = attsBySection.get(a.section) ?? [];
      arr.push(a);
      attsBySection.set(a.section, arr);
    }
  }
  const ticketsByItem = new Map<string, StandupItemTicket[]>();
  for (const t of tickets) {
    const arr = ticketsByItem.get(t.itemId) ?? [];
    arr.push(t);
    ticketsByItem.set(t.itemId, arr);
  }
  for (const arr of ticketsByItem.values()) arr.sort((a, b) => a.position - b.position);
  const itemsBySection = new Map<string, StandupItem[]>();
  for (const it of items) {
    const arr = itemsBySection.get(it.section) ?? [];
    arr.push(it);
    itemsBySection.set(it.section, arr);
  }
  for (const arr of itemsBySection.values()) arr.sort((a, b) => a.position - b.position);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={`standup ${formatDate(standup.date)}`}
        title={authorName}
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <Link to="/standups" className="btn btn-ghost btn-sm">
              <ArrowLeft className="size-4" /> Standups
            </Link>
            {me?.id === standup.userId && (
              <Link
                to="/standups/new"
                search={{ edit: standup.id }}
                className="btn btn-ghost btn-sm"
              >
                <Pencil className="size-4" /> Editar
              </Link>
            )}
            {me?.devMode && me.id === standup.userId && (
              <>
                <button
                  type="button"
                  onClick={() => repost.mutate()}
                  disabled={repost.isPending}
                  className="btn btn-ghost btn-sm"
                  title="Solo en dev — re-postear este standup a Mattermost"
                >
                  {repost.isPending && <span className="loading loading-spinner loading-xs" />}
                  <Send className="size-4" /> Re-postear (dev)
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (
                      window.confirm(
                        `¿Borrar definitivamente este standup? Se eliminarán items, menciones, tickets y adjuntos.`,
                      )
                    ) {
                      del.mutate();
                    }
                  }}
                  disabled={del.isPending}
                  className="btn btn-error btn-sm"
                  title="Solo en dev — el botón no aparece en producción"
                >
                  {del.isPending && <span className="loading loading-spinner loading-xs" />}
                  <Trash2 className="size-4" /> Borrar (dev)
                </button>
              </>
            )}
          </div>
        }
      />

      <div
        className={
          replies.length > 0
            ? "grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,520px)] gap-4 items-start"
            : "max-w-3xl"
        }
      >
        <div className="space-y-4 min-w-0">
          {SECTIONS.map((s) => {
            if (s.kind === "items") {
              const sectionItems = itemsBySection.get(s.key) ?? [];
              if (sectionItems.length === 0) return null;
              return (
                <section key={s.key} className="card-elevated p-5 space-y-3">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-primary">
                    {s.label}
                  </h3>
                  <ul className="space-y-2">
                    {sectionItems.map((it) => {
                      const mentionIds = mentionsByItem.get(it.id) ?? [];
                      const project = it.panelProjectId ? projectById.get(it.panelProjectId) : null;
                      const itemTickets = ticketsByItem.get(it.id) ?? [];
                      return (
                        <li
                          key={it.id}
                          className="rounded-lg border border-base-300/60 p-3 space-y-1.5"
                        >
                          {it.text && it.text.trim() && (
                            <p className="text-sm text-base-content/85 whitespace-pre-wrap leading-relaxed">
                              {it.text}
                            </p>
                          )}
                          <div className="flex items-center gap-1.5 flex-wrap">
                            {project && (
                              <Link
                                to="/projects/$projectId"
                                params={{ projectId: project.id }}
                                className="badge badge-primary badge-sm inline-flex items-center gap-1"
                              >
                                <Briefcase className="size-3" />
                                {project.name}
                              </Link>
                            )}
                            {itemTickets.map((t) => {
                              const num =
                                t.projectIdentifier && t.sequenceId !== null
                                  ? `${t.projectIdentifier}-${t.sequenceId}`
                                  : null;
                              return (
                                <a
                                  key={t.id}
                                  href={t.issueUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="badge badge-info badge-sm inline-flex items-center gap-1"
                                >
                                  🎫 {num ? `${num} · ` : ""}
                                  {t.issueTitleSnapshot.slice(0, 60)}
                                  <ExternalLink className="size-2.5" />
                                </a>
                              );
                            })}
                            {mentionIds.length > 0 && (
                              <span className="badge badge-ghost badge-sm inline-flex items-center gap-1">
                                <AtSign className="size-3" />
                                {mentionIds.map((id) => userById.get(id)?.name ?? "—").join(", ")}
                              </span>
                            )}
                          </div>
                          <AttachmentsGrid attachments={attsByItem.get(it.id) ?? []} />
                        </li>
                      );
                    })}
                  </ul>
                </section>
              );
            }
            // Texto libre. Mapeamos la key del form al section enum de la BD
            // para mirar attachments. Si hay texto o attachments, mostramos.
            const sectionKey = (
              {
                yesterdayBlockers: "yesterday_blockers",
                generalDifficulties: "general_difficulties",
                todayBlockers: "today_blockers",
                toolingFriction: "tooling_friction",
                comments: "comments",
              } as const
            )[s.key];
            const sectionAtts = attsBySection.get(sectionKey) ?? [];
            const raw = standup[s.key];
            const hasMeaningfulText = !!raw && !!raw.trim() && raw.trim().toLowerCase() !== "n/a";
            if (!hasMeaningfulText && sectionAtts.length === 0) return null;
            return (
              <section key={s.key} className="card-elevated p-5 space-y-3">
                <h3 className="text-xs font-semibold uppercase tracking-wider text-primary">
                  {s.label}
                </h3>
                {hasMeaningfulText && (
                  <p className="text-sm text-base-content/85 whitespace-pre-wrap leading-relaxed">
                    {raw}
                  </p>
                )}
                <AttachmentsGrid attachments={sectionAtts} />
              </section>
            );
          })}
        </div>

        {/* Replies del thread de Mattermost — sidebar pegado a la derecha
            en xl+, apilado debajo en pantallas más chicas. La aside es
            flujo natural (alineada al top del grid row); el wrapper
            interno hace el sticky para que el scroll del page-content
            no rompa la alineación inicial con la columna izquierda. */}
        {replies.length > 0 && (
          <aside className="min-w-0">
            <div className="xl:sticky xl:top-4 xl:max-h-[calc(100dvh-6rem)] xl:overflow-y-auto">
              <section className="card-elevated p-5 space-y-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <h3 className="text-xs font-semibold uppercase tracking-wider text-primary">
                    Respuestas del equipo · {replies.length}
                  </h3>
                  {standupPermalink && (
                    <a
                      href={standupPermalink}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-ghost btn-xs gap-1.5"
                      title="Abrir el thread en Mattermost"
                    >
                      <MessageSquare className="size-3.5" />
                      Ver en Mattermost
                      <ExternalLink className="size-3 opacity-60" />
                    </a>
                  )}
                </div>
                <ul className="space-y-2">
                  {replies.map((r) => {
                    const inner = (
                      <>
                        <div className="flex items-baseline justify-between gap-3 mb-1">
                          <span className="text-sm font-medium truncate">
                            {r.authorDisplayName || `@${r.authorUsername}`}
                          </span>
                          <span className="text-[10px] text-base-content/55 tabular-nums shrink-0">
                            {new Date(r.createdAtMm).toLocaleString("es-MX", {
                              day: "numeric",
                              month: "short",
                              hour: "numeric",
                              minute: "2-digit",
                            })}
                            {r.updatedAtMm && r.updatedAtMm > r.createdAtMm && (
                              <span className="ml-1 italic">· editado</span>
                            )}
                          </span>
                        </div>
                        <p className="text-sm text-base-content/85 whitespace-pre-wrap leading-relaxed">
                          {r.message}
                        </p>
                      </>
                    );
                    return (
                      <li key={r.id}>
                        {r.permalink ? (
                          <a
                            href={r.permalink}
                            target="_blank"
                            rel="noreferrer"
                            className="block rounded-lg border border-base-300/60 bg-base-100 p-3 hover:border-primary/40 hover:bg-base-200/60 transition-colors"
                            title="Abrir en Mattermost"
                          >
                            {inner}
                          </a>
                        ) : (
                          <div className="rounded-lg border border-base-300/60 bg-base-100 p-3">
                            {inner}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </section>
            </div>
          </aside>
        )}
      </div>
    </div>
  );
}
